<!--src/components/Common/SearchInput.vue-->
<script setup>
import { ref, computed, defineProps, defineEmits, watch, onMounted, nextTick, onUnmounted } from 'vue'
// import { useRoute } from 'vue-router'
import { getHotSearchTags } from '@/data/hot-search-data'
// import { DEFAULT_TAB_LIST } from '@/config/tabConfig'
import { ElMessage } from "element-plus";

// 引入搜索历史工具函数
import {
  saveSearchHistory,
  getSearchHistory,
  deleteSingleHistory,
  clearAllSearchHistory
} from '@/utils/searchUtil'

// 引入搜索联想API
import { getSearchSuggestions } from '@/api/search'

// const route = useRoute()

// 定义 props
const props = defineProps({
  defaultKey: { type: String, default: 'jd' },
  currentKey: { type: String, default: '' },
  isSticky: { type: Boolean, default: false },
  carouselInterval: { type: Number, default: 3000 },
  placeholder: { type: String, default: '搜索喜欢的商品...' },
  dropdownOptions: { type: Array, default: () => [] },
  keyword: { type: String, default: '' }
})

const emit = defineEmits(['search', 'switch-platform', 'handleKeydown'])

// --- 恢复数据逻辑 ---
const hotSearchList = getHotSearchTags('common')
const searchValue = ref(props.keyword || '')
const currentIndex = ref(0)
const dynamicPlaceholder = ref('')
const isInputFocused = ref(false)
const searchHistory = ref([])
const hotGoodsList = ref(hotSearchList) // 恢复热门推荐数据

// 搜索联想词
const suggestions = ref([])
const suggestionsLoading = ref(false)
let debounceTimer = null
const DEBOUNCE_DELAY = 300

// 监听搜索输入，防抖获取联想词
watch(() => searchValue.value, (newVal) => {
  // 清除之前的定时器
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }

  // 如果输入为空，清除联想词
  if (!newVal || newVal.trim().length === 0) {
    suggestions.value = []
    suggestionsLoading.value = false
    return
  }

  // 防抖处理
  debounceTimer = setTimeout(() => {
    fetchSuggestions(newVal.trim())
  }, DEBOUNCE_DELAY)
})

// 获取搜索联想词
const fetchSuggestions = async (keyword) => {
  if (!keyword) return
  suggestionsLoading.value = true
  console.log('[SearchInput] fetchSuggestions called with:', keyword)
  try {
    const res = await getSearchSuggestions({ keyword })
    console.log('[SearchInput] getSearchSuggestions result:', res)
    // 后端返回数据结构: { code: 0, message: "success", data: { suggestions: [...], total: N } }
    // axios返回: res.data = { code: 0, data: { suggestions: [...] } }, so res.data.data = { suggestions: [...] }
    if (res && res.data && res.data.data && res.data.data.suggestions && Array.isArray(res.data.data.suggestions)) {
      suggestions.value = res.data.data.suggestions.slice(0, 10)
      console.log('[SearchInput] suggestions updated:', suggestions.value)
    } else {
      console.log('[SearchInput] suggestions not valid, res:', res)
      suggestions.value = []
    }
  } catch (e) {
    console.log('[SearchInput] fetchSuggestions error:', e)
    suggestions.value = []
  } finally {
    suggestionsLoading.value = false
  }
}

// 平台逻辑
const selectedKey = ref(props.currentKey || props.defaultKey)

// const dropdownOptions = computed(() => {
//   return props.dropdownOptions.length > 0
//     ? props.dropdownOptions
//     : DEFAULT_TAB_LIST.map(p => ({ key: p.key, name: p.name }))
// })

const selectedSearchType = computed(() => {
  const matched = props.dropdownOptions.find(item => item.key === selectedKey.value)
  return matched?.name || '东啊东'
})

// 监听 currentKey 变化，同步更新 selectedKey（核心：实现外部驱动内部选中值）
watch(() => props.currentKey, (newVal) => {
  if (newVal) { // 仅当外部传递有效值时才更新，避免覆盖用户手动选择
    selectedKey.value = newVal
  }
}, { immediate: true })

watch(() => props.defaultKey, (newVal) => {
  if (!props.currentKey && newVal) {
    selectedKey.value = newVal
  }
}, { immediate: true })

// 监听外部 keyword
watch(() => props.keyword, (val) => {
  searchValue.value = val
})

watch(() => selectedKey.value, (newVal) => {
  const platform = props.dropdownOptions.find(p => p.key === newVal)
  if (platform) emit('switch-platform', platform)
})

// 轮播逻辑
let carouselTimer = null
const updatePlaceholder = () => {
  if (hotSearchList.length === 0) return
  if (!searchValue.value.trim() && !isInputFocused.value) {
    currentIndex.value = (currentIndex.value + 1) % hotSearchList.length
    dynamicPlaceholder.value = hotSearchList[currentIndex.value]
  }
}

// 搜索逻辑
const validateAndTriggerSearch = (keyword) => {
  const validKeyword = keyword.trim()
  if (!validKeyword) {
    ElMessage.warning('请输入搜索内容')
    return false
  }

  saveSearchHistory(validKeyword)
  fetchSearchHistory() // 搜索后刷新历史

  // 修复：确保平台信息完整传递（包含 key 和 dataKey）
  const platform = props.dropdownOptions.find(p => p.key === selectedKey.value) || { key: selectedKey.value }
  
  // 如果平台对象没有 dataKey，使用 key 作为 dataKey
  if (!platform.dataKey) {
    platform.dataKey = platform.key
  }

  emit('search', {
    keyword: validKeyword,
    platform: platform
  })

  isInputFocused.value = false
  return true
}

const handleSearch = () => {
  // 如果用户没输入，用当前轮播词搜索
  const keyword = searchValue.value.trim() || dynamicPlaceholder.value
  validateAndTriggerSearch(keyword)
}

const handleKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    // 如果用户没输入，用当前轮播词搜索
    const keyword = searchValue.value.trim() || dynamicPlaceholder.value
    validateAndTriggerSearch(keyword)
  }
}

// 点击轮播词
const handleCarouselKeywordClick = () => {
  if (dynamicPlaceholder.value) {
    searchValue.value = dynamicPlaceholder.value
    validateAndTriggerSearch(dynamicPlaceholder.value)
  }
}

// --- 恢复历史记录逻辑 ---
const fetchSearchHistory = () => {
  searchHistory.value = getSearchHistory()
}

const handleInputFocus = () => {
  isInputFocused.value = true
  dynamicPlaceholder.value = '' // 聚焦时清空轮播词，避免和输入文字重叠
  clearInterval(carouselTimer) // 停止轮播定时器
  carouselTimer = null
  fetchSearchHistory()
}

const handleInputBlur = () => {
  setTimeout(() => {
    isInputFocused.value = false
    // 失焦后恢复轮播（如果没有用户输入）
    if (!searchValue.value.trim() && hotSearchList.length > 0) {
      carouselTimer = setInterval(updatePlaceholder, props.carouselInterval)
    }
  }, 200)
}

const handlePanelMouseLeave = () => {
  // 直接设置 isInputFocused 为 false，触发面板 v-show 隐藏
  isInputFocused.value = false;
}

const handleHistoryItemClick = (keyword) => {
  searchValue.value = keyword
  validateAndTriggerSearch(keyword)
}

const handleHotItemClick = (keyword) => {
  searchValue.value = keyword
  validateAndTriggerSearch(keyword)
}

const handleSuggestionClick = (keyword) => {
  searchValue.value = keyword
  suggestions.value = []  // 清空联想词
  validateAndTriggerSearch(keyword)
}

const handleDeleteSingleHistory = (keyword, e) => {
  e.stopPropagation()
  deleteSingleHistory(keyword)
  fetchSearchHistory()
}

const handleClearAllHistory = (e) => {
  e.stopPropagation()
  clearAllSearchHistory()
  fetchSearchHistory()
}

onMounted(async () => {
  await nextTick()
  // Bootstrap 5 会自动初始化带有 data-bs-toggle="dropdown" 的元素，无需手动初始化
  
  // 鼠标移出下拉菜单时自动隐藏
  const dropdownEl = document.getElementById(`searchTypeDropdown-${props.isSticky ? 'sticky' : 'default'}`)
  if (dropdownEl) {
    dropdownEl.addEventListener('shown.bs.dropdown', () => {
      const dropdownMenu = dropdownEl.nextElementSibling
      if (dropdownMenu && dropdownMenu.classList.contains('dropdown-menu')) {
        dropdownMenu.addEventListener('mouseleave', () => {
          const dropdownInstance = window.bootstrap?.Dropdown?.getInstance(dropdownEl)
          if (dropdownInstance) {
            dropdownInstance.hide()
          }
        })
      }
    })
  }
  
  fetchSearchHistory()
  updatePlaceholder()
  carouselTimer = setInterval(updatePlaceholder, props.carouselInterval)
})

// let carouselTimer = null
onUnmounted(() => {
  clearInterval(carouselTimer)
})
</script>

<template>
  <div
    class="search-wrapper"
    :class="{ 'sticky-mode': isSticky }"
  >
    <!-- 搜索框容器：保持原有结构 -->
    <div class="search-box-container">
      <!-- 1. 平台切换 -->
      <div class="platform-selector">
        <button
          :id="`searchTypeDropdown-${isSticky ? 'sticky' : 'default'}`"
          class="btn dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          {{ selectedSearchType }}
        </button>
        <ul class="dropdown-menu">
          <li
            v-for="item in dropdownOptions"
            :key="item.key"
          >
            <a
              class="dropdown-item"
              href="javascript:void(0)"
              @click="selectedKey = item.key"
            >
              {{ item.name }}
            </a>
          </li>
        </ul>
      </div>

      <!-- 2. 输入框区域 -->
      <div class="input-area">
        <input
          v-model="searchValue"
          type="text"
          class="form-control"
          :placeholder="searchValue ? '' : (dynamicPlaceholder || placeholder)"
          @focus="handleInputFocus"
          @blur="handleInputBlur"
          @keydown="handleKeydown"
        >
        <!-- 轮播覆盖层 -->
        <div
          v-if="!searchValue && dynamicPlaceholder && !isInputFocused"
          class="carousel-overlay"
          @click="handleCarouselKeywordClick"
        >
          {{ dynamicPlaceholder }}
        </div>
      </div>

      <!-- 3. 搜索按钮 -->
      <button
        class="search-btn"
        @click="handleSearch"
      >
        <i class="bi bi-search" />
        <span>搜索</span>
      </button>
    </div>

    <!-- 搜索历史/热门面板：恢复 v-show 逻辑 -->
    <div
      v-show="isInputFocused && (suggestions.length > 0 || searchHistory.length > 0 || hotGoodsList.length > 0)"
      class="search-panel"
      @mouseleave="handlePanelMouseLeave"
    >
      <!-- 搜索联想词 -->
      <div
        v-if="suggestions.length > 0"
        class="suggestion-group"
      >
        <div class="header">
          搜索建议
        </div>
        <div class="suggestion-list">
          <div
            v-for="(item, index) in suggestions"
            :key="'s'+index"
            class="suggestion-item"
            :class="{ 'border-top': index > 0 }"
            @click="handleSuggestionClick(item.keyword)"
          >
            <svg
              class="suggestion-icon"
              viewBox="0 0 1024 1024"
              width="16"
              height="16"
            >
              <path
                d="M942.314667 856.768l-206.784-206.784a360.533333 360.533333 0 0 0 66.773333-209.472 360.533333 360.533333 0 0 0-105.984-255.786667 360.490667 360.490667 0 0 0-255.786667-105.962666A360.490667 360.490667 0 0 0 184.725333 184.746667a360.469333 360.469333 0 0 0-105.962666 255.786666A360.533333 360.533333 0 0 0 184.746667 696.32a360.576 360.576 0 0 0 255.786666 105.962667 360.554667 360.554667 0 0 0 207.893334-65.642667l207.018666 207.018667 86.890667-86.890667zM729.130667 440.533333a286.72 286.72 0 0 1-84.522667 204.053334 286.72 286.72 0 0 1-204.074667 84.544 286.72 286.72 0 0 1-204.074666-84.544A286.72 286.72 0 0 1 151.893333 440.533333a286.72 286.72 0 0 1 84.544-204.074666A286.72 286.72 0 0 1 440.533333 151.893333a286.72 286.72 0 0 1 204.074667 84.544 286.72 286.72 0 0 1 84.522667 204.074667z"
                fill="#828A9A"
              />
            </svg>
            <span class="suggestion-keyword">{{ item.keyword }}</span>
          </div>
        </div>
      </div>

      <!-- 历史记录 -->
      <div
        v-if="searchHistory.length"
        class="history-group"
      >
        <div class="header">
          <span>搜索历史</span>
          <span
            class="clear-btn"
            @click="handleClearAllHistory"
          >清空</span>
        </div>
        <div class="tag-list">
          <span
            v-for="(item, index) in searchHistory"
            :key="'h'+index"
            class="tag-item"
            @click="handleHistoryItemClick(item)"
          >
            {{ item }}
            <i
              class="bi bi-x-circle"
              @click.stop="handleDeleteSingleHistory(item, $event)"
            />
          </span>
        </div>
      </div>

      <!-- 热门推荐 -->
      <div
        v-if="hotGoodsList.length"
        class="hot-group"
      >
        <div class="header">
          热门推荐
        </div>
        <div class="tag-list">
          <span
            v-for="(item, index) in hotGoodsList"
            :key="'g'+index"
            class="tag-item"
            @click="handleHotItemClick(item)"
          >
            {{ item }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/css/mixin.scss"; // 引入 Mixin

.search-wrapper {
  width: 100%;
  position: relative;
  z-index: 10;

  // --- 主容器：修复按钮错位，确保 Flex 不换行 ---
  .search-box-container {
    display: flex;
    align-items: center;
    width: 100%;
    height: 38px;
    border: 2px solid;
    @include border_color; // 应用主题边框
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
    flex-wrap: nowrap; // 强制不换行

    // 聚焦高亮
    &:has(.form-control:focus) {
      box-shadow: 0 0 0 3px rgba(var(--active-bg-color-rgb, 255, 0, 0), 0.2);
    }
  }

  // --- 1. 平台选择 ---
  .platform-selector {
    flex: 0 0 auto;
    padding-left: 2px;

    .btn {
      height: 34px;
      padding: 0 8px;
      font-size: 14px;
      color: #333;
      background: transparent;
      border: none;
      &:hover { color: var(--active-bg-color, #ff0000); }
    }
  }

  // --- 2. 输入框 ---
  .input-area {
    flex: 1; // 关键：输入框占据剩余空间，挤压按钮不换行
    height: 100%;
    position: relative;
    min-width: 0; // 防止 flex 子元素溢出

    .form-control {
      height: 100%;
      width: 100%;
      padding: 0 10px;
      border: none;
      background: transparent;
      font-size: 14px;
      outline: none !important;
      box-shadow: none !important;
      &:focus {
        outline: none !important;
        box-shadow: none !important;
        border: none !important;
      }
    }

    .carousel-overlay {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 14px;
      pointer-events: auto;
      cursor: pointer;
    }
  }

  // --- 3. 搜索按钮 ---
  .search-btn {
    flex: 0 0 auto; // 固定宽度
    height: 34px;
    width: 70px;
    @include bg_color; // 应用主题背景
    @include font_color; // 应用主题文字
    border: none;
    border-radius: 0 4px 4px 0;
    cursor: pointer;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.9;
    }
  }

  .search-btn i {
    position: relative;
    margin-right: 4px;
    font-size: 14px;
  }

  .search-btn span {
    position: relative;
    pointer-events: none;
  }

  .search-btn[style*="width: 40px"] span { display: none; }

  // --- 4. 右侧占位 ---
  //.search-right-placeholder {
  //  flex: 0 0 80px; // 保留宽度
  //  height: 100%;
  //  background: transparent !important; // 透明
  //}

  // --- 面板样式 ---
  .search-panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    padding: 15px;
    z-index: 9999;
    border: 1px solid #eee;

    .header {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: #666;
      margin-bottom: 10px;
      font-weight: bold;
    }

    .clear-btn {
      font-weight: normal;
      color: #999;
      cursor: pointer;
      &:hover { color: var(--active-bg-color, #ff0000); }
    }

    .tag-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 15px;

      .tag-item {
        background: #f5f5f5;
        padding: 4px 12px;
        border-radius: 16px;
        font-size: 13px;
        color: #333;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;

        &:hover {
          background: #fef0f0;
          color: var(--active-bg-color, #ff0000);
        }
      }
    }

    .suggestion-list {
      display: flex;
      flex-direction: column;
    }

    .suggestion-item {
      display: flex;
      align-items: center;
      padding: 8px 0;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: #fef0f0;
      }

      &.border-top {
        border-top: 1px solid #f0f0f0;
      }

      .suggestion-icon {
        width: 16px;
        height: 16px;
        margin-right: 10px;
        flex-shrink: 0;
      }

      .suggestion-keyword {
        flex: 1;
        font-size: 13px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

// --- 吸顶模式 ---
.sticky-mode {
  .search-box-container {
    height: 34px;
    border-width: 1px;
  }
  .search-btn { height: 32px; width: 60px; font-size: 14px; }
}

// --- 小屏适配 ---
@media (max-width: 991.98px) {
  .search-wrapper {
    width: 90%;
    margin: 0 auto;
    //padding: 0 5px;

    .search-box-container {
      height: 34px;
    }

    // 小屏按钮仅显示图标
    .search-btn {
      width: 40px;
      span { display: none; }
    }
  }
  .search-panel{
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    font-size: 12px;
    width: 100%;
    margin: 0 auto;
  }

}
</style>
